/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import {
  AliasType,
  ComponentManagerFn,
  ViewAttributeChooserProp,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './MutliSelectLOVField';
import { baseFieldProps, fontStyleProps } from '../TextField/BaseField';
import { isNotEmpty } from 'utils/isEmpty';

const MutliSelectLOVFieldManager: ComponentManagerFn<'MutliSelectLOVField'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'MutliSelectLOVField'>(
      platform,
      'MutliSelectLOVField',
    ),
    type: 'MutliSelectLOVField',
    name: 'MultiSelect LOV Field',
    isChildAllowed: (page, item, childType, { appUid, pageId }) => {
      if (
        !(
          [
            'TriggerOnChange',
            'LOVGrid',
            'LOVMappings',
            'TriggerAfterServerValidate',
          ] as ItemType[]
        ).includes(childType)
      ) {
        return false;
      }
      const children = platform.getChildrenOfType(
        appUid,
        pageId,
        page,
        item,
        childType,
      );
      return children.length === 0 ? true : 'Already added';
    },
    defaultChildren: ['LOVGrid', 'LOVMappings'],
    defaultValue,
    props: {
      ...baseFieldProps,
      ...fontStyleProps,
      viewAttribute: {
        ...(baseFieldProps.viewAttribute as ViewAttributeChooserProp),
        getAttributeDataTypes: () => [
          'Email',
          'Phone',
          'Reference',
          'String',
          'UserID',
        ],
      },
      comboDatasourceAlias: {
        label: 'LOV Data Store Alias',
        type: 'DataSourceAliasChooser',
        category: 'Data',
        allowedTypes: ['ReadOnlyDataStore'],
        required: true,
      },
      doNotPrefetch: {
        label: 'Do Not Prefetch',
        type: 'boolean',
        category: 'Data',
        helpText: 'Enable this to prevent blank query on LOV open',
      },
      returnValueAttribute: {
        label: 'Return Value Attribute',
        type: 'ComboAttributeChooser',
        aliasType: 'LOV',
        category: 'Data',
        required: true,
        getAttributeDataTypes: () => 'ALL',
      },
      returnDisplayAttribute: {
        label: 'Return Display Attribute',
        type: 'ComboAttributeChooser',
        aliasType: 'LOV',
        category: 'Data',
        required: true,
        getAttributeDataTypes: () => 'ALL',
      },
      open: {
        label: 'Open',
        type: 'boolean',
      },
      popupWidth: {
        label: 'Popup Width',
        type: 'number',
        min: 400,
        max: 1200,
        step: 40,
      },
      popupHeight: {
        label: 'Popup Height',
        type: 'number',
        min: 200,
        max: 800,
        step: 40,
      },
      delimiter: {
        label: 'Delimeter',
        type: 'string',
        helpText:
          'Set a sequence of one or more characters to seperate the selected values. Default: ~',
      },
      showGridFilters: {
        label: 'Show Grid Filters',
        type: 'boolean',
      },
      placeholder: {
        label: 'Placeholder',
        type: 'string',
      },
      serverValidateOnChange: {
        label: 'Server Validate On Change',
        type: 'boolean',
        category: 'Data',
      },
    },

    getDataSourceAliasProperty: (aliasType: AliasType) =>
      aliasType === 'LOV' ? { aliasProp: 'comboDatasourceAlias' } : undefined,
    isDataSourceAliasUsed: (item, alias) =>
      item.props.comboDatasourceAlias === alias,
    isPropertyVisible(prop, itemData): boolean {
      switch (prop) {
        case 'returnValueAttribute':
          return isNotEmpty(itemData.comboDatasourceAlias);
        case 'open':
          return false;
        default:
          return true;
      }
    },
    getDisplayText: (item) =>
      (typeof item.props.label === 'string'
        ? item.props.label
        : item.props.label.value) ?? '',
    //   analyze: analyzeLOVField,
    devStyles: { display: 'inline-flex' },
    category: 'Forms',
    icon: { iconName: 'io-lov-field', iconType: 'cloudio' },
    excludeChildren: true,
    generateShortId: (item) =>
      typeof item.label === 'string' ? item.label : item.label?.value,
    description:
      'LOV (List of Values) is simple Pop up which is used to display the associated values while inserting values in forms. You need to configure LOV with respectively associated datasource, return value attribute and the binding view attribute. Add the required columns as the children to the LOV to be shown on the popup.',
  };
};

export default MutliSelectLOVFieldManager;
